import React, { useState } from 'react';

// Per-platform logout URLs so the modal can offer a direct "log out" link.
// Social OAuth auto-connects whatever account is already signed in, with no way
// (on TikTok/X especially) to force an account chooser — so to add a DIFFERENT
// account the user must log out of the platform first.
const LOGOUT_URLS = {
  tiktok: 'https://www.tiktok.com/logout',
  facebook: 'https://www.facebook.com/logout.php',
  instagram: 'https://www.instagram.com/accounts/logout/',
  threads: 'https://www.threads.net/logout',
  twitter: 'https://twitter.com/logout',
};

const PLATFORM_LABELS = {
  tiktok: 'TikTok',
  facebook: 'Facebook',
  instagram: 'Instagram',
  threads: 'Threads',
  twitter: 'X',
};

/**
 * Modal shown around the "adding another account" flow.
 *
 * Two variants (via `variant`):
 *  - "pre":  shown BEFORE launching OAuth when the user already has an account
 *            on this platform. Primary action = Continue (launches OAuth).
 *  - "already": shown AFTER OAuth when the platform silently returned the same
 *            account. Primary action = OK / dismiss.
 *
 * Props:
 *  - open (bool), onClose (fn)
 *  - platform (string key, e.g. 'tiktok')
 *  - variant ('pre' | 'already')
 *  - onContinue (fn) — only used for the 'pre' variant (launch OAuth)
 *  - onRetry (fn) — only used for the 'already' variant. Relaunches OAuth after
 *    the user has logged out of the platform, so they can pick a different
 *    account. Enables the two-step "log out → connect again" button.
 */
const AddAccountModal = ({ open, onClose, platform, variant = 'pre', onContinue, onRetry }) => {
  // Two-step state for the 'already' variant: false = "log out & connect",
  // true = user has opened logout, button flips to "connect again" (relaunch).
  const [loggedOut, setLoggedOut] = useState(false);

  if (!open) return null;

  const label = PLATFORM_LABELS[platform] || 'this platform';
  const logoutUrl = LOGOUT_URLS[platform];

  // Step 1: open the platform's logout in a new tab, then flip the button.
  const handleLogoutStep = () => {
    if (logoutUrl) {
      window.open(logoutUrl, '_blank', 'noopener,noreferrer');
    }
    setLoggedOut(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl max-w-md w-full p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {variant === 'pre' ? (
          <>
            <h3 className="text-xl font-bold text-ink mb-2">
              Add a different {label} account
            </h3>
            <p className="text-sm text-ink-muted mb-4">
              {label} will automatically connect the account you're currently
              signed into. If you want to add a <strong>different</strong> account,
              log out of {label} first (or use an incognito window), then continue —
              you'll be prompted to choose which account to connect.
            </p>
            <p className="text-sm text-ink-muted mb-5">
              Connecting the account you're already signed into? Just press Continue.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              {logoutUrl && (
                <a
                  href={logoutUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-center border border-ink text-ink px-4 py-2.5 rounded-lg font-medium hover:bg-surface-raised transition min-h-[var(--tap-target)] sm:min-h-0"
                >
                  Log out of {label} ↗
                </a>
              )}
              <button
                onClick={onContinue}
                className="flex-1 bg-accent text-accent-ink px-4 py-2.5 rounded-lg font-medium hover:bg-accent-hover transition min-h-[var(--tap-target)] sm:min-h-0"
              >
                Continue →
              </button>
            </div>
            <button
              onClick={onClose}
              className="w-full text-sm text-ink-muted mt-4 underline hover:text-ink"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <div className="text-5xl mb-3 text-center">ℹ️</div>
            <h3 className="text-xl font-bold text-ink mb-2 text-center">
              Same account — already connected
            </h3>
            <p className="text-sm text-ink-muted mb-4">
              This is the {label} account you're currently signed into, which is
              already connected — we refreshed it. To add a{' '}
              <strong>different</strong> account, log out of {label} and connect
              again so {label} lets you choose another account.
            </p>

            {!loggedOut ? (
              <>
                {/* Step 1 */}
                <button
                  onClick={handleLogoutStep}
                  className="w-full bg-black text-white px-4 py-2.5 rounded-lg font-medium hover:bg-accent-hover transition min-h-[var(--tap-target)] sm:min-h-0"
                >
                  Log out of {label} & connect another ↗
                </button>
                <p className="text-xs text-ink-muted text-center mt-2">
                  Opens {label} logout in a new tab — come back here afterward.
                </p>
              </>
            ) : (
              <>
                {/* Step 2 — after logout, relaunch OAuth */}
                <button
                  onClick={() => onRetry?.()}
                  className="w-full bg-black text-white px-4 py-2.5 rounded-lg font-medium hover:bg-accent-hover transition min-h-[var(--tap-target)] sm:min-h-0"
                >
                  Logged out — connect again →
                </button>
                <p className="text-xs text-ink-muted text-center mt-2">
                  Make sure you logged out in the other tab, then press to choose a
                  different {label} account.
                </p>
              </>
            )}

            <button
              onClick={onClose}
              className="w-full text-sm text-ink-muted mt-4 underline hover:text-ink"
            >
              {loggedOut ? 'Cancel' : 'No thanks — go to dashboard'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AddAccountModal;
